import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../auth/AuthContext";
import { runBigQueryQuery } from "../bigquery/BigQueryClient";
import { buildFlightsSQL, type Flight } from "./experimentFlights";
import {
  BID_COST_EUR_MICROS,
  CLICKHOUSE_METRICS,
  fetchPerBudgetRatios,
  PER_BUDGET_METRICS,
  RATIO_PERCENTILES,
  type PerBudgetRatios,
  type RatioStat,
} from "./perBudgetRatio";

/**
 * Experiment report — per-flight KPIs queried directly from the WPR metrics tables in
 * BigQuery (`remerge-dwh.wpr_dashboard.raw_metrics` + `raw_events`), sliced on
 * `group_type='primary_prediction_flight_id'` and scoped to the experiment window. Flight
 * metadata (name, is_control) comes from BigQuery too. Pure BigQuery — no DuckDB/parquet,
 * no Druid — so this renders identically in dev and on the static/remote deployment.
 *
 * The big `*_eur_sum`/`impression_value_impressions_sum` aggregates exceed JS's
 * MAX_SAFE_INTEGER; the BigQuery REST client returns them as strings and coerces with
 * Number(), so they never trip the "not safe to convert to a number" path.
 *
 * Conversion / revenue / reengagement metrics use the 1-day (D1) cohort, gated to days old
 * enough for the cohort to have closed (mirrors the dashboard's export query).
 */

export type MetricKey =
  | "bids"
  | "impressions"
  | "unique_users_impressions"
  | "ap_reengagements"
  | "ap_conversions"
  | "revenue_eur"
  | "ap_revenue_eur"
  | "impression_value_eur"
  | "ap_roas"
  | "ap_roas_d7"
  | "ap_xpc"
  | "partner_xpc"
  | "partner_xpc_d7"
  | "supply_cost_eur"
  | "selectivity"
  | "impval_supply_ratio"
  | "impval_supply_infra_ratio"
  | "maxbid_supply_ratio"
  | "maxbid_supply_impressions"
  | "mse_win_rate"
  | "mse_win_rate_wins"
  | "mse_win_rate_impressions"
  | "reeng_logloss_1d"
  | "ap_reeng_logloss_1d";

type Direction = "up" | "down" | "neutral" | "target"; // which direction is "good" ("target" = closest to an ideal value)
type Format = "int" | "eur" | "pct" | "ratio" | "mse";

interface MetricDef {
  key: MetricKey;
  label: string;
  section: "Volume" | "Performance" | "Model & efficiency";
  format: Format;
  good: Direction;
  note?: string; // shown when the value is unavailable in the live source
  devOnly?: boolean; // sourced from a cluster only the dev-server proxy can reach
  guardrail?: boolean; // spend metric — flag if it diverges between arms
  target?: number; // ideal value for good: "target" metrics (default 1)
}

const METRICS: MetricDef[] = [
  { key: "bids", label: "Bids", section: "Volume", format: "int", good: "neutral" },
  { key: "impressions", label: "Impressions", section: "Volume", format: "int", good: "neutral" },
  {
    key: "unique_users_impressions",
    label: "Unique users (impressions)",
    section: "Volume",
    format: "int",
    good: "neutral",
    devOnly: true,
    note: "Distinct users served at least one impression over the whole window, not a sum of daily counts. Approximate.",
  },
  {
    key: "impression_value_eur",
    label: "Impression value",
    section: "Volume",
    format: "eur",
    good: "neutral",
    devOnly: true,
    note: "Estimated impression value summed over confirmed impressions. An estimate the bidder produced, not money received.",
  },
  {
    key: "ap_reengagements",
    label: "AP reengagements (D1)",
    section: "Volume",
    format: "int",
    good: "neutral",
    note: "MMP-attributed reengagements within 1 day of bid (D1 cohort), summed over the experiment window.",
  },
  {
    key: "ap_conversions",
    label: "AP conversions (D1)",
    section: "Volume",
    format: "int",
    good: "neutral",
    note: "MMP-attributed conversions within 1 day of bid (D1 cohort), summed over the experiment window. Longer windows aren't shown since they only fully populate once the cohort closes.",
  },
  {
    key: "revenue_eur",
    label: "Total revenue",
    section: "Performance",
    format: "eur",
    good: "neutral",
    guardrail: true,
  },
  {
    key: "ap_revenue_eur",
    label: "AP customer revenue (D1)",
    section: "Performance",
    format: "eur",
    good: "neutral",
    note: "MMP-attributed in-app customer revenue within 1 day of bid (D1 cohort), summed over the window.",
  },
  {
    key: "ap_roas",
    label: "AP ROAS (D1)",
    section: "Performance",
    format: "pct",
    good: "up",
    note: "MMP-attributed in-app revenue within 1 day of bid (D1 cohort) ÷ client cost, summed over the window.",
  },
  {
    key: "ap_roas_d7",
    label: "AP ROAS (D7)",
    section: "Performance",
    format: "pct",
    good: "up",
    note: "MMP-attributed in-app revenue within 7 days of bid (D7 cohort) ÷ client cost, over bid days whose 7-day window has elapsed.",
  },
  {
    key: "ap_xpc",
    label: "AP XPC (D1, conv / €)",
    section: "Performance",
    format: "ratio",
    good: "up",
    note: "MMP-attributed conversions within 1 day of bid (D1 cohort) per € of client cost, summed over the window.",
  },
  {
    key: "partner_xpc",
    label: "AP XPC (D1, partner conv / €)",
    section: "Performance",
    format: "ratio",
    good: "up",
    devOnly: true,
    note: "Partner-reported conversions within 1 day of bid (D1 cohort) per € of client cost.",
  },
  {
    key: "partner_xpc_d7",
    label: "AP XPC (D7, partner conv / €)",
    section: "Performance",
    format: "ratio",
    good: "up",
    devOnly: true,
    note: "Partner-reported conversions within 7 days of bid (D7 cohort) per € of client cost, over bid days whose 7-day window has elapsed.",
  },
  {
    key: "supply_cost_eur",
    label: "Supply cost",
    section: "Performance",
    format: "eur",
    good: "neutral",
    guardrail: true,
  },
  {
    key: "selectivity",
    label: "Selectivity",
    section: "Model & efficiency",
    format: "ratio",
    good: "up",
    note: "Supply-weighted geometric mean of 1/pacing_adjustment over the experiment window (WPR / BigQuery).",
  },
  {
    key: "impval_supply_ratio",
    label: "Impression value / supply cost",
    section: "Model & efficiency",
    format: "ratio",
    good: "up",
    note: "Estimated impression value on won impressions ÷ supply cost (WPR / BigQuery).",
  },
  {
    key: "impval_supply_infra_ratio",
    label: "Impression value / (supply cost + infra)",
    section: "Model & efficiency",
    format: "ratio",
    good: "up",
    note: "Estimated impression value on won impressions ÷ (supply cost + infrastructure cost), where infrastructure cost is bids × BID_COST_EUR_MICROS (WPR / BigQuery). Break-even is 1.",
  },
  {
    key: "maxbid_supply_ratio",
    label: "Max bid / supply cost",
    section: "Model & efficiency",
    format: "ratio",
    good: "up",
    note: "Average max bid (CPM) ÷ supply CPM; the max-bid average is over all bids, so it estimates the impression-side sum from the bid-side mean.",
  },
  {
    key: "maxbid_supply_impressions",
    label: "Max bid [impressions] / supply cost",
    section: "Model & efficiency",
    format: "ratio",
    good: "up",
    devOnly: true,
    note: "Average max bid ÷ supply CPM, both over confirmed impressions so the two share a population.",
  },
  {
    key: "mse_win_rate",
    label: "MSE win-rate prediction",
    section: "Model & efficiency",
    format: "mse",
    good: "down",
    note: "Mean squared error of win-rate predictions (WPR / BigQuery), bids-weighted over the window.",
  },
  {
    key: "mse_win_rate_wins",
    label: "MSE win-rate prediction vs wins",
    section: "Model & efficiency",
    format: "mse",
    good: "down",
    devOnly: true,
    note: "Mean squared error of the CPM regressor's win-rate prediction against whether the bid won the auction, over bids the predictor was used on.",
  },
  {
    key: "mse_win_rate_impressions",
    label: "MSE win-rate prediction vs impressions",
    section: "Model & efficiency",
    format: "mse",
    good: "down",
    devOnly: true,
    note: "Same prediction scored against whether the bid produced a confirmed impression rather than a won auction — a stricter outcome, since roughly half of won auctions never render.",
  },
  {
    key: "reeng_logloss_1d",
    label: "Reengagement log-loss 1d",
    section: "Model & efficiency",
    format: "mse",
    good: "down",
    note: "Cross-entropy of the reengagement model's predictions vs observed 1d self-attributed reengagements, over the window. Lower is better. Approximate (daily observed rate vs daily average predicted rate).",
  },
  {
    key: "ap_reeng_logloss_1d",
    label: "AP Reengagement log-loss 1d",
    section: "Model & efficiency",
    format: "mse",
    good: "down",
    note: "Cross-entropy of the reengagement model's predictions vs observed 1d MMP-attributed reengagements, over the window. Lower is better. Approximate (daily observed rate vs daily average predicted rate).",
  },
];

const SECTIONS: MetricDef["section"][] = ["Performance", "Volume", "Model & efficiency"];

const PER_BUDGET_TIP =
  "Test divided Control ratio of this row's metric. >1 means test is better. All aggregates " +
  "weighted by control flight's budget revenue. The AP ROAS rows are further restricted to " +
  "budgets with a ROAS goal; every other row uses all budgets, as do the arm columns.";

/** Legend for the numbers in each cell; the median is the one to read first. */
const RATIO_LEGEND = (
  <span className="experiment-report__ratio-legend">
    geom. mean [min, p1, p5, <b>p50</b>, p95, p99, max]
  </span>
);

const PER_BUDGET_DEV_ONLY =
  "Sourced from Druid and ClickHouse, both reachable only through the dev-server proxy, so this column is not shown in production.";

interface ArmReport {
  flightId: string;
  name: string;
  isControl: boolean;
  metrics: Partial<Record<MetricKey, number | null>>;
}

interface ReportData {
  arms: ArmReport[];
}

interface ReportProps {
  experimentId: string;
  startTime: string | null;
  endTime: string | null;
  /** Experiment metadata line rendered at the bottom of the overview card. */
  header?: ReactNode;
  /** Experiment description, rendered in its own box to the right of the overview. */
  description?: ReactNode;
}

/* ──────────────────────── formatting ──────────────────────── */

const numFmt = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });
const eurFmt = new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

function formatValue(format: Format, v: number | null | undefined): string {
  if (v == null) return "–";
  switch (format) {
    case "int":
      return numFmt.format(v);
    case "eur":
      return eurFmt.format(v);
    case "pct":
      return `${Math.round(v * 100)}%`;
    case "ratio":
      return v.toFixed(2);
    case "mse":
      return v.toFixed(4);
  }
}

/** Date → `YYYY-MM-DD` (UTC) for the BigQuery `bid_date` filter. */
function toDateStr(d: Date): string {
  return d.toISOString().split("T")[0];
}

/** Flight names carry a lot of redundant prefix on the experiment page — strip it. */
function cleanArmName(name: string, flightId: string): string {
  const cleaned = (name || "")
    .replace(/^\(copy of\)\s*/i, "") // "(Copy of) "
    .replace(/\[[^\]]*\]\s*/g, "") // "[Experiment name] "
    .replace(/[:\-–]\s*\d+\s*$/, "") // trailing ": 6853" / "- 6853"
    .trim();
  return cleaned || `Flight ${flightId}`;
}

function sqlList(values: string[]): string {
  return values.map(v => `'${String(v).replace(/'/g, "''")}'`).join(",");
}

/* ──────────────────────── data (WPR metrics, BigQuery) ──────────────────────── */

// One BigQuery row per flight: core arm aggregates + reengagement log-loss inputs.
interface ReportRow {
  flight: string;
  bids: number | null;
  impressions: number | null;
  buying_micros: number | null;
  buying_closed_micros: number | null;
  supply_micros: number | null;
  ap_conversions: number | null;
  ap_rev_1d: number | null;
  ap_rev_7d: number | null;
  buying_closed7_micros: number | null;
  impval_sum: number | null;
  adjusted_bid_micros: number | null;
  win_rate_mse: number | null;
  selectivity: number | null;
  ap_reeng_count: number | null;
  // Reengagement log-loss components (self + AP), over days where the predictor was active.
  reeng_obs: number | null;
  reeng_imp: number | null;
  reeng_pred_num: number | null;
  ap_reeng_obs: number | null;
  ap_reeng_imp: number | null;
  ap_reeng_pred_num: number | null;
}

const RAW_METRICS = "`remerge-dwh.wpr_dashboard.raw_metrics`";
const RAW_EVENTS = "`remerge-dwh.wpr_dashboard.raw_events`";

// Per-flight arm aggregates over the experiment window, straight from the WPR metrics tables.
//
// raw_metrics is one row per (bid_date, flight); raw_events appends many rows per day (one per
// pipeline run) and must be SUM-collapsed to one row per (bid_date, flight) BEFORE the join —
// otherwise the join fans out and inflates every metric. This mirrors the dashboard's export
// query (`wpr_dashboard_metrics_1d.sql`) that builds the parquet.
//
// Conversion / revenue / reengagement counts use the 1-day (D1) cohort, gated to bid_dates old
// enough to have closed; the model log-loss inputs are restricted to days the predictor was on.
function buildReportSQL(flightIds: string[], startDate: string, endDate: string): string {
  const ids = sqlList(flightIds);
  // Bounds the metrics whose conversions need time to arrive: XPC, ROAS, AP conversions and AP
  // partner conversions. Within such a row it covers numerator and denominator alike, so a rate
  // never divides matured conversions by unmatured revenue. Volume and bid-time metrics are
  // complete once their bid day ends and span the whole window.
  const closed = "j.bid_date < DATE_SUB(CURRENT_DATE(), INTERVAL 1 DAY)"; // D1 cohort has closed
  const closed7 = "j.bid_date < DATE_SUB(CURRENT_DATE(), INTERVAL 7 DAY)"; // D7 cohort has closed
  const okSelf =
    "estimated_impression_reengagement_rate_mean > 0 AND NOT IS_NAN(estimated_impression_reengagement_rate_mean)";
  const okAp =
    "estimated_impression_ap_reengagement_rate_mean > 0 AND NOT IS_NAN(estimated_impression_ap_reengagement_rate_mean)";
  return `
    WITH ev AS (
      SELECT bid_date, group_type, dim_val1, dim_val2,
        SUM(ap_conversions_1d_sum) AS ap_conversions_1d_sum,
        SUM(ap_customer_revenue_eur_1d_sum) AS ap_customer_revenue_eur_1d_sum,
        SUM(ap_customer_revenue_eur_7d_sum) AS ap_customer_revenue_eur_7d_sum,
        SUM(ap_reengagements_1d_sum) AS ap_reengagements_1d_sum,
        SUM(reengagements_1d_sum) AS reengagements_1d_sum
      FROM ${RAW_EVENTS}
      WHERE group_type='primary_prediction_flight_id'
        AND dim_val1 IN (${ids})
        AND bid_date >= DATE '${startDate}' AND bid_date <= DATE '${endDate}'
      GROUP BY bid_date, group_type, dim_val1, dim_val2
    ),
    j AS (
      SELECT
        m.dim_val1 AS flight, m.bid_date,
        m.bids_sum, m.impressions_sum, m.buying_price_eur_sum, m.win_price_eur_sum,
        m.impression_value_impressions_sum, m.selectivity, m.win_rate_mse, m.adjusted_bid_mean,
        m.estimated_impression_reengagement_rate_mean,
        m.estimated_impression_ap_reengagement_rate_mean,
        ev.ap_conversions_1d_sum, ev.ap_customer_revenue_eur_1d_sum, ev.ap_customer_revenue_eur_7d_sum,
        ev.ap_reengagements_1d_sum, ev.reengagements_1d_sum
      FROM ${RAW_METRICS} m
      LEFT JOIN ev
        ON m.bid_date = ev.bid_date AND m.group_type = ev.group_type
        AND COALESCE(m.dim_val1, '') = COALESCE(ev.dim_val1, '')
        AND COALESCE(m.dim_val2, '') = COALESCE(ev.dim_val2, '')
      WHERE m.group_type='primary_prediction_flight_id'
        AND m.dim_val1 IN (${ids})
        AND m.bid_date >= DATE '${startDate}' AND m.bid_date <= DATE '${endDate}'
    )
    SELECT
      flight,
      SUM(bids_sum) AS bids,
      SUM(impressions_sum) AS impressions,
      SUM(buying_price_eur_sum) AS buying_micros,
      SUM(CASE WHEN ${closed} THEN buying_price_eur_sum END) AS buying_closed_micros,
      SUM(win_price_eur_sum) AS supply_micros,
      SUM(impression_value_impressions_sum) AS impval_sum,
      SUM(CASE WHEN win_rate_mse IS NOT NULL THEN win_rate_mse * bids_sum END)
        / NULLIF(SUM(CASE WHEN win_rate_mse IS NOT NULL THEN bids_sum END), 0) AS win_rate_mse,
      SUM(CASE WHEN adjusted_bid_mean IS NOT NULL THEN adjusted_bid_mean * bids_sum END)
        / NULLIF(SUM(CASE WHEN adjusted_bid_mean IS NOT NULL THEN bids_sum END), 0) AS adjusted_bid_micros,
      SUM(CASE WHEN ${closed} THEN ap_conversions_1d_sum END) AS ap_conversions,
      SUM(CASE WHEN ${closed} THEN ap_customer_revenue_eur_1d_sum END) AS ap_rev_1d,
      SUM(CASE WHEN ${closed7} THEN ap_customer_revenue_eur_7d_sum END) AS ap_rev_7d,
      SUM(CASE WHEN ${closed7} THEN buying_price_eur_sum END) AS buying_closed7_micros,
      EXP(
        SUM(CASE WHEN selectivity > 0 THEN LN(selectivity) * win_price_eur_sum END)
        / NULLIF(SUM(CASE WHEN selectivity > 0 THEN win_price_eur_sum END), 0)
      ) AS selectivity,
      SUM(CASE WHEN ${closed} THEN ap_reengagements_1d_sum END) AS ap_reeng_count,
      SUM(CASE WHEN ${okSelf} THEN reengagements_1d_sum END) AS reeng_obs,
      SUM(CASE WHEN ${okSelf} THEN impressions_sum END) AS reeng_imp,
      SUM(CASE WHEN ${okSelf} THEN estimated_impression_reengagement_rate_mean * impressions_sum END) AS reeng_pred_num,
      SUM(CASE WHEN ${okAp} THEN ap_reengagements_1d_sum END) AS ap_reeng_obs,
      SUM(CASE WHEN ${okAp} THEN impressions_sum END) AS ap_reeng_imp,
      SUM(CASE WHEN ${okAp} THEN estimated_impression_ap_reengagement_rate_mean * impressions_sum END) AS ap_reeng_pred_num
    FROM j
    GROUP BY flight
  `;
}

const toNum = (v: unknown): number | null => (v == null ? null : Number(v));

// Pooled binary cross-entropy from window-summed observed reengagements and the
// impression-weighted predicted rate. Mirrors the dashboard's reengagement log-loss.
function reengLogLoss(obs: number | null, imp: number | null, predNum: number | null): number | null {
  if (obs == null || imp == null || !imp || predNum == null) return null;
  const obsRate = obs / imp;
  const predRate = predNum / imp;
  if (!(predRate > 0)) return null;
  const p = Math.max(Math.min(predRate, 1 - 1e-6), 1e-6);
  return -(obsRate * Math.log(p) + (1 - obsRate) * Math.log(1 - p));
}

async function fetchReport(flights: Flight[], startDate: string, endDate: string): Promise<ReportData> {
  const flightIds = flights.map(f => f.flight_id);
  const rows = await runBigQueryQuery(buildReportSQL(flightIds, startDate, endDate));
  const byFlight = new Map(rows.map(row => [String(row.flight), row as unknown as ReportRow]));
  const arms: ArmReport[] = flights.map(f => {
    const r = byFlight.get(f.flight_id);
    const buying = toNum(r?.buying_micros);
    // The D1 rates divide by revenue over the same closed bid days their numerators count, so
    // a window too recent for the cohort to have elapsed reads as no data, not as a zero rate.
    const buyingClosed = toNum(r?.buying_closed_micros);
    const supply = toNum(r?.supply_micros);
    const impval = toNum(r?.impval_sum);
    const adjBid = toNum(r?.adjusted_bid_micros);
    const impressions = toNum(r?.impressions);
    const bids = toNum(r?.bids);
    const apConv = toNum(r?.ap_conversions);
    const apRev = toNum(r?.ap_rev_1d);
    const apRev7 = toNum(r?.ap_rev_7d);
    const buyingClosed7 = toNum(r?.buying_closed7_micros);
    return {
      flightId: f.flight_id,
      name: f.name,
      isControl: f.is_control,
      metrics: {
        bids,
        impressions,
        revenue_eur: buying != null ? buying / 1e6 : null,
        supply_cost_eur: supply != null ? supply / 1e6 : null,
        // AP (MMP-attributed) metrics: D1 cohort (1 day post-bid), summed over the window.
        ap_conversions: apConv,
        ap_revenue_eur: apRev != null ? apRev / 1e6 : null,
        ap_roas: apRev != null && buyingClosed ? apRev / buyingClosed : null,
        ap_roas_d7: apRev7 != null && buyingClosed7 ? apRev7 / buyingClosed7 : null,
        ap_xpc: apConv != null && buyingClosed ? (apConv * 1e6) / buyingClosed : null,
        ap_reengagements: toNum(r?.ap_reeng_count),
        selectivity: toNum(r?.selectivity),
        impval_supply_ratio: impval != null && supply ? impval / 1e9 / (supply / 1e6) : null,
        // Same ratio with infrastructure cost in the denominator: every bid costs
        // BID_COST_EUR_MICROS whether or not it won, while only impressions cost supply.
        impval_supply_infra_ratio:
          impval != null && supply != null && bids != null && supply + bids * BID_COST_EUR_MICROS > 0
            ? impval / ((supply + bids * BID_COST_EUR_MICROS) * 1000)
            : null,
        maxbid_supply_ratio: adjBid != null && supply && impressions ? (adjBid * impressions) / (supply * 1000) : null,
        mse_win_rate: toNum(r?.win_rate_mse),
        reeng_logloss_1d: reengLogLoss(toNum(r?.reeng_obs), toNum(r?.reeng_imp), toNum(r?.reeng_pred_num)),
        ap_reeng_logloss_1d: reengLogLoss(toNum(r?.ap_reeng_obs), toNum(r?.ap_reeng_imp), toNum(r?.ap_reeng_pred_num)),
      },
    };
  });

  return { arms };
}

/* ──────────────────────── rendering helpers ──────────────────────── */

function deltaInfo(
  value: number | null | undefined,
  control: number | null | undefined,
  good: Direction,
): { label: string; cls: string; pct: number } | null {
  if (value == null || control == null || control === 0) return null;
  const pct = ((value - control) / Math.abs(control)) * 100;
  if (!Number.isFinite(pct)) return null;
  const improved = good === "up" ? pct >= 0 : good === "down" ? pct <= 0 : null;
  const cls = improved == null ? "" : improved ? "entity-list__change--up" : "entity-list__change--down";
  return { label: `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`, cls, pct };
}

/** For "target" metrics: signed deviation from the ideal value (e.g. 0.97 → −3% vs 1.0). */
function targetCell(target: number, value: number | null | undefined) {
  if (value == null || target === 0) return null;
  const pct = ((value - target) / target) * 100;
  if (!Number.isFinite(pct)) return null;
  return (
    <span className="entity-list__change">
      {" "}
      ({pct >= 0 ? "+" : ""}
      {pct.toFixed(1)}% vs ideal)
    </span>
  );
}

/** Arm closest to a target value (smallest |value − target|); null unless ≥2 arms have data. */
function closestToTarget(arms: ArmReport[], key: MetricKey, target: number): string | null {
  let best: string | null = null;
  let bestDist = Infinity;
  let withData = 0;
  for (const a of arms) {
    const v = a.metrics[key];
    if (v == null) continue;
    withData += 1;
    const dist = Math.abs(v - target);
    if (dist < bestDist) {
      bestDist = dist;
      best = a.flightId;
    }
  }
  return withData >= 2 ? best : null;
}

function deltaCell(def: MetricDef, value: number | null | undefined, control: number | null | undefined) {
  const d = deltaInfo(value, control, def.good);
  if (!d) return null;
  return <span className={`entity-list__change ${d.cls}`}> ({d.label})</span>;
}

// Spend metrics that confound the comparison if they diverge between arms.
const SPEND_DIVERGENCE_PCT = 5;

/**
 * Inline indicator (⚠ / ▲) with a hover/focus tooltip rendered to document.body via a
 * portal with position: fixed — so no ancestor's overflow (or the table) can clip it.
 */
function Tip({ icon, text, className, label }: { icon: string; text: string; className: string; label: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const open = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const halfW = 130; // half of the tooltip max-width, for viewport clamping
    const left = Math.min(Math.max(r.left + r.width / 2, 8 + halfW), window.innerWidth - 8 - halfW);
    setPos({ top: r.bottom + 6, left });
  };
  const close = () => setPos(null);

  return (
    <span
      ref={ref}
      className={className}
      tabIndex={0}
      aria-label={label}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
    >
      {" "}
      {icon}
      {pos &&
        createPortal(
          <span className="experiment-report__tip-pop" style={{ top: pos.top, left: pos.left }}>
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}

/** A ⚠ marker when a guardrail (spend) metric diverges more than the threshold vs control. */
function guardrailMark(def: MetricDef, value: number | null | undefined, control: number | null | undefined) {
  if (!def.guardrail) return null;
  const d = deltaInfo(value, control, def.good);
  if (!d || Math.abs(d.pct) <= SPEND_DIVERGENCE_PCT) return null;
  return (
    <Tip
      icon="⚠"
      className="experiment-report__warn"
      label="Spend divergence warning"
      text={`Spend differs ${d.label} vs control (>${SPEND_DIVERGENCE_PCT}%) — performance comparison may be confounded`}
    />
  );
}

/** The metric the verdict compares arms on. The per-experiment evaluation_criterion
 *  column was dropped from `global_experiments`, so every report is judged on profit. */
interface PrimaryMetric {
  label: string;
  good: Direction;
  format: (v: number) => string;
  value: (m: ArmReport["metrics"]) => number | null;
}

const PRIMARY_METRIC: PrimaryMetric = {
  label: "Remerge Profit",
  good: "up",
  format: v => eurFmt.format(v),
  value: m => (m.revenue_eur != null && m.supply_cost_eur != null ? m.revenue_eur - m.supply_cost_eur : null),
};

/* ──────────────────────── component ──────────────────────── */

export default function ExperimentReport({ experimentId, startTime, endTime, header, description }: ReportProps) {
  const { isAuthenticated, requiresAuth } = useAuth();
  const [flights, setFlights] = useState<Flight[]>([]);
  const [report, setReport] = useState<ReportData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [perBudget, setPerBudget] = useState<PerBudgetRatios | null>(null);
  const [perBudgetError, setPerBudgetError] = useState<string | null>(null);

  const { startDate, endDate } = useMemo(() => {
    const start = startTime ? new Date(startTime) : null;
    const rawEnd = endTime ? new Date(endTime) : null;
    const now = new Date();
    const end = rawEnd && rawEnd < now ? rawEnd : now;
    if (!start || Number.isNaN(start.getTime())) return { startDate: null, endDate: null };
    return { startDate: toDateStr(start), endDate: toDateStr(end) };
  }, [startTime, endTime]);

  // Load the experiment's flights once (cheap; gives real arm names + control marker).
  useEffect(() => {
    if (requiresAuth && !isAuthenticated) return;
    let cancelled = false;
    let sql: string;
    try {
      sql = buildFlightsSQL(experimentId);
    } catch (e) {
      setError(String(e));
      return;
    }
    runBigQueryQuery(sql)
      .then(res => {
        if (cancelled) return;
        const fl = (res as unknown as { flight_id: string; name: string; is_control: boolean }[]).map(f => ({
          flight_id: String(f.flight_id),
          name: f.name,
          is_control: Boolean(f.is_control),
        }));
        setFlights(fl);
      })
      .catch(err => !cancelled && setError(String(err)));
    return () => {
      cancelled = true;
    };
  }, [experimentId, isAuthenticated, requiresAuth]);

  // Fetch the per-flight report straight from the WPR metrics tables in BigQuery once
  // flights and the window are ready.
  useEffect(() => {
    if (flights.length === 0 || !startDate || !endDate) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    fetchReport(flights, startDate, endDate)
      .then(r => !cancelled && setReport(r))
      .catch(err => !cancelled && setError(String(err)))
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [flights, startDate, endDate]);

  // Reachable only through the dev-server proxy, which is where `import.meta.env.DEV` is
  // true. Loads independently, so a failure here leaves the rest of the table intact.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (flights.length === 0 || !startDate || !endDate) return;
    let cancelled = false;
    // StrictMode mounts the effect twice in dev, and these queries run for minutes, so the
    // discarded run is aborted rather than left competing for the clusters.
    const controller = new AbortController();
    setPerBudgetError(null);
    // The partial lands as soon as Druid returns; the rest follows from ClickHouse.
    fetchPerBudgetRatios(flights, startDate, endDate, partial => !cancelled && setPerBudget(partial), controller.signal)
      .then(r => !cancelled && setPerBudget(r))
      .catch(err => !cancelled && setPerBudgetError(String(err)));
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [flights, startDate, endDate]);

  const orderedArms = useMemo(() => {
    if (!report) return [];
    return [...report.arms].sort((a, b) => Number(b.isControl) - Number(a.isControl));
  }, [report]);
  const controlArm = orderedArms.find(a => a.isControl) ?? orderedArms[0];
  const primary = PRIMARY_METRIC;
  const testArms = orderedArms.filter(a => !a.isControl);

  // Guardrail: spend (revenue / supply cost) diverging >5% from control confounds the
  // performance comparison. Collect any breaches to flag prominently.
  const spendBreaches = METRICS.filter(m => m.guardrail).flatMap(m => {
    const c = controlArm?.metrics[m.key];
    if (c == null || c === 0) return [];
    return testArms.flatMap(a => {
      const d = deltaInfo(a.metrics[m.key], c, m.good);
      return d && Math.abs(d.pct) > SPEND_DIVERGENCE_PCT ? [{ metric: m.label, arm: a, label: d.label }] : [];
    });
  });
  // Spend breaches grouped by arm, for the ⚠ shown against that arm in the verdict.
  const breachByArm = new Map<string, string[]>();
  for (const b of spendBreaches) {
    const list = breachByArm.get(b.arm.flightId) ?? [];
    list.push(`${b.metric} ${b.label}`);
    breachByArm.set(b.arm.flightId, list);
  }

  const ArmLabel = ({ arm }: { arm: ArmReport }) => {
    const name = cleanArmName(arm.name, arm.flightId);
    // The "control" chip is redundant when the flight name already says it.
    const showTag = arm.isControl && !/control/i.test(name);
    return (
      <span className="experiment-report__arm">
        <span className="experiment-report__arm-name">{name}</span>
        {showTag && <span className="experiment-report__control-tag">control</span>}
        <span className="experiment-report__arm-id">#{arm.flightId}</span>
      </span>
    );
  };

  const ready = !isLoading && !error && report && orderedArms.length > 0;
  // Needs a fetchable source and a control to compare against.
  const showPerBudget = import.meta.env.DEV && testArms.length > 0 && !!controlArm;

  return (
    <div className="experiment-report">
      {/* ── Top row: overview (verdict + meta) on the left, description box on the right.
             Values are point estimates — significance comes from the bootstrapping engine. ── */}
      <div className="experiment-report__top-row">
        <div className="entity-detail__card experiment-report__overview">
          <div className="experiment-report__overview-top">
            <div className="experiment-report__verdict-head">
              <span className="experiment-report__verdict-eyebrow">Primary metric</span>
              <h2 className="experiment-report__verdict-metric">{primary.label}</h2>
            </div>
          </div>

          {isLoading && (
            <div className="experiment-report__skeleton">
              <div className="chart-placeholder experiment-report__skeleton-bar experiment-report__skeleton-bar--label" />
              <div className="chart-placeholder experiment-report__skeleton-bar" />
              <div className="chart-placeholder experiment-report__skeleton-bar" />
            </div>
          )}

          {!isLoading && error && (
            <>
              <p className="experiment-detail__prose">Could not load the report.</p>
              <p className="entity-list__error-detail">{error}</p>
              <p className="experiment-detail__prose experiment-report__hint">
                Per-flight metrics come from the WPR metrics (BigQuery) source, aggregated over the experiment window.
              </p>
            </>
          )}

          {ready && (
            <div className="experiment-report__verdict-row">
              {controlArm && (
                <div className="experiment-report__verdict-baseline">
                  <div className="experiment-report__verdict-arm">
                    {cleanArmName(controlArm.name, controlArm.flightId)}
                  </div>
                  <div className="experiment-report__verdict-base">
                    {(() => {
                      const v = primary.value(controlArm.metrics);
                      return v == null ? "–" : primary.format(v);
                    })()}
                  </div>
                  <div className="experiment-report__verdict-sub">control · baseline</div>
                </div>
              )}
              {testArms.map(arm => {
                const v = primary.value(arm.metrics);
                const c = controlArm ? primary.value(controlArm.metrics) : null;
                const d = deltaInfo(v, c, primary.good);
                return (
                  <div key={arm.flightId} className="experiment-report__verdict-arm-block">
                    <div className="experiment-report__verdict-arm">
                      {cleanArmName(arm.name, arm.flightId)}
                      {breachByArm.has(arm.flightId) && (
                        <Tip
                          icon="⚠"
                          className="experiment-report__warn"
                          label="Spend divergence warning"
                          text={`Spend diverged >${SPEND_DIVERGENCE_PCT}% vs control (${breachByArm
                            .get(arm.flightId)!
                            .join(", ")}) — this arm's result may be confounded`}
                        />
                      )}
                    </div>
                    <div className={`experiment-report__verdict-delta ${d?.cls ?? ""}`}>{d ? d.label : "—"}</div>
                    <div className="experiment-report__verdict-sub">
                      {v == null ? "–" : primary.format(v)} vs control
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Metadata line — supporting context, below the hero verdict. */}
          {header && <hr className="entity-detail__divider" />}
          {header}
        </div>

        {description && (
          <div className="entity-detail__card experiment-report__description">
            <p className="entity-detail__card-label">Description</p>
            {description}
          </div>
        )}
      </div>

      {ready && (
        <>
          {/* ── KPI comparison: metrics as rows, arms as columns, Δ vs control ── */}
          <div className="entity-detail__card">
            <p className="entity-detail__card-label">Arm comparison</p>
            <div className="experiment-report__scroll">
              <table className="entity-list__table experiment-report__table experiment-report__table--compare">
                <thead>
                  <tr>
                    <th>Metric</th>
                    {orderedArms.map(a => (
                      <Fragment key={a.flightId}>
                        <th
                          className="entity-list__col--right experiment-report__arm-col"
                          title={a.name || `Flight ${a.flightId}`}
                        >
                          <ArmLabel arm={a} />
                        </th>
                        {/* The ratio is defined against control, so only test arms carry it. */}
                        {showPerBudget && !a.isControl && (
                          <th className="entity-list__col--right experiment-report__ratio-col">
                            Per-budget test/control
                            <Tip
                              icon="ℹ"
                              className="experiment-report__info"
                              label="How the per-budget test/control ratio is computed"
                              text={PER_BUDGET_TIP}
                            />
                            <span className="section-header__tag" title={PER_BUDGET_DEV_ONLY}>
                              Dev only
                            </span>
                            {RATIO_LEGEND}
                          </th>
                        )}
                      </Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SECTIONS.map(section => (
                    <ReportSection
                      key={section}
                      section={section}
                      arms={orderedArms}
                      control={controlArm}
                      perBudget={showPerBudget ? perBudget : null}
                      perBudgetError={showPerBudget ? perBudgetError : null}
                      showPerBudget={showPerBudget}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {showPerBudget && perBudgetError && (
              <p className="entity-list__error-detail">Per-budget test/control failed: {perBudgetError}</p>
            )}
            {showPerBudget &&
              perBudget?.warnings.map(w => (
                <p key={w} className="entity-list__error-detail">
                  {w}
                </p>
              ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Weighted percentiles of the per-budget ratios, inline after the point estimate: a value near
 * 1 can mean the budgets agree or that they cancel out.
 */
function RatioSpread({ stat }: { stat: RatioStat }) {
  return (
    <span
      className="experiment-report__ratio-spread"
      title={`${stat.count} budget pools contributed a ratio for this metric`}
    >
      [
      {RATIO_PERCENTILES.map((p, i) => (
        <Fragment key={p}>
          {i > 0 && ", "}
          {p === 50 ? <b>{stat.percentiles[p].toFixed(2)}</b> : stat.percentiles[p].toFixed(2)}
        </Fragment>
      ))}
      ]
    </span>
  );
}

/**
 * One arm's aggregate per-budget ratio for one metric row. Colouring follows the metric's
 * `good` direction, as in the Δ-vs-control cells, since above 1 is not always better.
 */
function PerBudgetCell({
  def,
  perBudget,
  error,
  flightId,
}: {
  def: MetricDef;
  perBudget: PerBudgetRatios | null;
  error: string | null;
  flightId: string;
}) {
  if (!PER_BUDGET_METRICS.has(def.key)) {
    return (
      <td
        className="entity-list__col--right experiment-report__placeholder"
        title="No per-budget counterpart: this metric needs a per-bid prediction-and-outcome pair (or a supply weight) that sits on a different event row than the prediction, so it cannot be formed per budget."
      >
        –
      </td>
    );
  }
  // A failure has to read differently from a pending fetch.
  if (error) {
    return (
      <td className="entity-list__col--right experiment-report__placeholder" title={error}>
        ⚠
      </td>
    );
  }
  if (!perBudget) {
    return <td className="entity-list__col--right experiment-report__placeholder">loading…</td>;
  }
  const v = perBudget.byFlight.get(flightId)?.[def.key];
  // These arrive on their own slower query, so pending must not read as "no data".
  if (v == null && CLICKHOUSE_METRICS.has(def.key) && perBudget.warnings.length === 0) {
    return (
      <td
        className="entity-list__col--right experiment-report__placeholder"
        title="Runs a per-bid join; takes a minute or two on a multi-week window."
      >
        loading…
      </td>
    );
  }
  if (v == null) {
    return (
      <td
        className="entity-list__col--right experiment-report__placeholder"
        title="No budget had a usable ratio for this metric in both arms."
      >
        –
      </td>
    );
  }
  const improved = def.good === "up" ? v.value >= 1 : def.good === "down" ? v.value <= 1 : null;
  const cls = improved == null ? "" : improved ? "entity-list__change--up" : "entity-list__change--down";
  return (
    <td className="entity-list__col--right">
      <span className={cls}>{v.value.toFixed(2)}×</span>
      <RatioSpread stat={v} />
    </td>
  );
}

function ReportSection({
  section,
  arms,
  control,
  perBudget,
  perBudgetError,
  showPerBudget,
}: {
  section: MetricDef["section"];
  arms: ArmReport[];
  control: ArmReport | undefined;
  perBudget: PerBudgetRatios | null;
  perBudgetError: string | null;
  showPerBudget: boolean;
}) {
  const metrics = METRICS.filter(m => m.section === section && (!m.devOnly || showPerBudget));
  const ratioCols = showPerBudget ? arms.filter(a => !a.isControl).length : 0;
  return (
    <>
      <tr className="experiment-report__section-row">
        <td colSpan={arms.length + 1 + ratioCols}>{section}</td>
      </tr>
      {metrics.map(m => {
        // Arm values for these rows come with the per-budget payload, not the BigQuery report.
        const armValue = (a: ArmReport): number | null | undefined =>
          m.devOnly ? perBudget?.armMetrics.get(a.flightId)?.[m.key] : a.metrics[m.key];
        // "target" metrics are judged on closeness to an ideal, not vs control.
        const isTarget = m.good === "target";
        const targetVal = m.target ?? 1;
        const targetBest = isTarget ? closestToTarget(arms, m.key, targetVal) : null;
        // Unavailable metric (no data source yet): show "n/a" in each cell, no value/delta.
        const unavailable = !(m.devOnly && !perBudget) && !!m.note && arms.every(a => armValue(a) == null);
        return (
          <tr key={m.key} className="entity-list__row">
            <td className="entity-list__col--name" title={m.note}>
              {m.label}
            </td>
            {arms.map(a => {
              const ratioCell = showPerBudget && !a.isControl && (
                <PerBudgetCell def={m} perBudget={perBudget} error={perBudgetError} flightId={a.flightId} />
              );
              if (unavailable) {
                return (
                  <Fragment key={a.flightId}>
                    <td className="entity-list__col--right experiment-report__placeholder">n/a</td>
                    {ratioCell}
                  </Fragment>
                );
              }
              const v = armValue(a);
              const isBest = isTarget && targetBest === a.flightId;
              return (
                <Fragment key={a.flightId}>
                  <td className={`entity-list__col--right${isBest ? " experiment-report__best" : ""}`}>
                    {formatValue(m.format, v)}
                    {isTarget ? (
                      targetCell(targetVal, v)
                    ) : (
                      <>
                        {!a.isControl && deltaCell(m, v, control && armValue(control))}
                        {!a.isControl && guardrailMark(m, v, control && armValue(control))}
                      </>
                    )}
                    {isBest && (
                      <Tip
                        icon="▲"
                        className="experiment-report__best-mark"
                        label="Closest to ideal"
                        text={`Closest to ideal (${targetVal})`}
                      />
                    )}
                  </td>
                  {ratioCell}
                </Fragment>
              );
            })}
          </tr>
        );
      })}
    </>
  );
}
